import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/shared";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import NotFound from "@/pages/NotFound";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import ClientPortal from "@/pages/ClientPortal";
import Clients from "@/pages/Clients";
import Brands from "@/pages/Brands";
import MenuRecipes from "@/pages/MenuRecipes";
import Ingredients from "@/pages/Ingredients";
import Purchases from "@/pages/Purchases";
import Inventory from "@/pages/Inventory";
import Orders from "@/pages/Orders";
import QuickOrder from "@/pages/QuickOrder";
import AIImport from "@/pages/AIImport";
import AIUploadCentre from "@/pages/AIUploadCentre";
import Approvals from "@/pages/Approvals";
import MyRequests from "@/pages/MyRequests";
import Wastage from "@/pages/Wastage";
import Billing from "@/pages/Billing";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Spinner label="Authenticating" />;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function HomeRoute() {
  const { user } = useAuth();
  return user?.role === "client" ? <ClientPortal /> : <Dashboard />;
}

function RootRoute() {
  const { user, loading } = useAuth();
  if (loading) return <Spinner label="Loading" />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <Login />;
}

function RequireModule({ module, children }) {
  const { user, modules } = useAuth();
  if (user?.role !== "client") return children;
  if (modules === null) return <Spinner label="Loading" />;
  if (modules.includes(module)) return children;
  return (
    <div className="text-center py-24" data-testid="module-not-available">
      <p className="text-slate-200 font-medium text-lg">This section isn't available for your account.</p>
      <p className="text-sm text-muted-foreground mt-1">Please contact Dish Factory if you need access.</p>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" theme="dark" richColors closeButton />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RootRoute />} />
          <Route path="/login" element={<RootRoute />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/dashboard" element={<Protected><HomeRoute /></Protected>} />
          <Route path="/clients" element={<Protected><Clients /></Protected>} />
          <Route path="/brands" element={<Protected><Brands /></Protected>} />
          <Route path="/menu" element={<Protected><RequireModule module="menu"><MenuRecipes /></RequireModule></Protected>} />
          <Route path="/ingredients" element={<Protected><Ingredients /></Protected>} />
          <Route path="/purchases" element={<Protected><Purchases /></Protected>} />
          <Route path="/inventory" element={<Protected><RequireModule module="inventory"><Inventory /></RequireModule></Protected>} />
          <Route path="/orders" element={<Protected><RequireModule module="orders"><Orders /></RequireModule></Protected>} />
          <Route path="/quick-order" element={<Protected><QuickOrder /></Protected>} />
          <Route path="/ai-import" element={<Protected><AIImport /></Protected>} />
          <Route path="/upload-centre" element={<Protected><AIUploadCentre /></Protected>} />
          <Route path="/approvals" element={<Protected><Approvals /></Protected>} />
          <Route path="/my-requests" element={<Protected><MyRequests /></Protected>} />
          <Route path="/wastage" element={<Protected><RequireModule module="wastage"><Wastage /></RequireModule></Protected>} />
          <Route path="/billing" element={<Protected><RequireModule module="billing"><Billing /></RequireModule></Protected>} />
          <Route path="/reports" element={<Protected><Reports /></Protected>} />
          <Route path="/settings" element={<Protected><Settings /></Protected>} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
